(() => {
  'use strict';

  if (window.__lldgInquiryLoaded) {
    document.dispatchEvent(new CustomEvent('lldg:inquiry:refresh'));
    return;
  }
  window.__lldgInquiryLoaded = true;
  document.documentElement.classList.add('lldg-js');

  const ROOT_SELECTOR =
    '[data-lldg-inquiry], .inquiry-form-shell, [data-lldg-inquiry-form]';
  const PATHS = ['residential', 'trade', 'general'];
  const instances = new Map();
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const fieldName = (field) => (field.getAttribute('name') || '').toLowerCase();

  class LldgInquiry {
    constructor(root) {
      this.root = root;
      this.form = root.matches('form')
        ? root
        : root.querySelector(
            'form[data-lldg-inquiry-form], form[action*="/contact"], form',
          );
      if (!this.form) return;

      this.currentStep = 0;
      this.inquiryType = '';
      this.models = [];
      this.nextButton = null;
      this.backButton = null;
      this.submitButton = null;
      this.generatedError = null;
      this.editorPreviewActive = false;
      this.editorAlignTimer = null;
      this.boundClick = this.handleClick.bind(this);
      this.boundChange = this.handleChange.bind(this);
      this.boundSubmit = this.handleSubmit.bind(this);

      this.collect();
      this.form.addEventListener('click', this.boundClick);
      this.form.addEventListener('change', this.boundChange);
      this.form.addEventListener('submit', this.boundSubmit);

      const checked = this.pathInputs().find((input) => input.checked);
      if (checked) this.inquiryType = this.pathForInput(checked);

      const hasErrors = Boolean(
        root.querySelector(
          '[data-contact-errors], [data-form-errors], .form-errors, .form-system-message, .errors',
        ),
      );
      if (hasErrors) this.currentStep = this.inquiryType ? 2 : 0;

      const hashPath = this.pathFromHash(window.location.hash);
      if (hashPath && !hasErrors) {
        this.inquiryType = hashPath;
        this.currentStep = 1;
        this.checkPathInput(hashPath);
      }

      this.render(false);
    }

    collect() {
      const explicitPanels = Array.from(
        this.form.querySelectorAll('[data-inquiry-step], [data-form-step]'),
      );
      const panels = explicitPanels.length
        ? explicitPanels
        : Array.from(this.form.querySelectorAll('.form-step'));

      const numericSteps = panels
        .map((panel) =>
          Number(panel.dataset.inquiryStep || panel.dataset.formStep),
        )
        .filter(Number.isFinite);
      const oneBased = numericSteps.length > 0 && !numericSteps.includes(0);

      this.models = panels.map((panel, index) => {
        const declared = panel.dataset.inquiryStep || panel.dataset.formStep || '';
        let step = this.parseStep(declared, oneBased);
        let path = this.normalizePath(
          panel.dataset.inquiryPath || panel.dataset.inquiryType || panel.dataset.path || '',
        );

        if (!Number.isFinite(step)) {
          const inferred = this.inferPanel(panel, index, panels.length);
          step = inferred.step;
          if (!path) path = inferred.path;
        }

        panel.dataset.lldgOriginalHidden = String(panel.hidden);
        panel.dataset.lldgOriginalActive = String(panel.classList.contains('is-active'));
        return {
          panel,
          step,
          path,
          blockId: panel.dataset.inquiryBlockId || '',
          kind: panel.dataset.stepKind || '',
        };
      });

      this.nextButton = this.form.querySelector(
        '[data-inquiry-next], [data-form-next], .form-next',
      );
      this.backButton = this.form.querySelector(
        '[data-inquiry-back], [data-form-back], .form-back',
      );
      this.submitButton = this.form.querySelector(
        '[data-inquiry-submit], [data-form-submit]',
      );
      if (this.nextButton) {
        this.nextButton.dataset.lldgOriginalType = this.nextButton.getAttribute('type') || '';
        if (!this.submitButton) this.nextButton.setAttribute('type', 'submit');
      }
    }

    parseStep(value, oneBased) {
      if (value === '') return NaN;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return oneBased ? numeric - 1 : numeric;
      const normalized = String(value).toLowerCase();
      if (['choice', 'choose', 'type', 'start'].includes(normalized)) return 0;
      if (['details', 'detail', 'project', 'company'].includes(normalized)) return 1;
      if (['contact', 'finish', 'message'].includes(normalized)) return 2;
      return NaN;
    }

    inferPanel(panel, index, total) {
      const names = Array.from(panel.querySelectorAll('input, select, textarea'))
        .map(fieldName)
        .join(' ');
      const text = panel.textContent.toLowerCase();

      if (panel.querySelector('input[type="radio"]') && /inquiry|residential|trade/.test(text)) {
        return { step: 0, path: '' };
      }
      if (/company|website|product.category|trade.reason/.test(`${names} ${text}`)) {
        return { step: 1, path: 'trade' };
      }
      if (/project.location|project.type|timeline|investment/.test(`${names} ${text}`)) {
        return { step: 1, path: 'residential' };
      }
      if (/subject/.test(names) && !/message|body/.test(names)) {
        return { step: 1, path: 'general' };
      }
      if (/email/.test(names) && /message|body/.test(names)) {
        return { step: 2, path: '' };
      }
      if (index === 0) return { step: 0, path: '' };
      if (index === total - 1) return { step: 2, path: '' };
      return { step: 1, path: '' };
    }

    normalizePath(value) {
      const normalized = String(value || '').trim().toLowerCase();
      if (PATHS.includes(normalized)) return normalized;
      if (normalized.startsWith('residential')) return 'residential';
      if (normalized.startsWith('trade')) return 'trade';
      if (normalized.startsWith('general')) return 'general';
      return '';
    }

    pathFromHash(hash) {
      if (hash === '#project-inquiry') return 'residential';
      if (hash === '#trade-inquiry') return 'trade';
      if (hash === '#general-inquiry') return 'general';
      return '';
    }

    pathInputs() {
      return Array.from(this.form.querySelectorAll('input[type="radio"]')).filter(
        (input) => this.pathForInput(input),
      );
    }

    pathForInput(input) {
      const choice = input.closest('[data-inquiry-choice]');
      return this.normalizePath(
        input.dataset.inquiryPath ||
          (choice && choice.dataset.inquiryChoice) ||
          input.value,
      );
    }

    checkPathInput(path) {
      this.pathInputs().forEach((input) => {
        input.checked = this.pathForInput(input) === path;
      });
    }

    firstAvailablePath() {
      const model = this.models.find((candidate) => candidate.path);
      return model ? model.path : 'residential';
    }

    modelFromEditorEvent(event) {
      const target = event.target instanceof Element
        ? event.target.closest('[data-inquiry-block-id]')
        : null;
      if (target && this.form.contains(target)) {
        return this.models.find((model) => model.panel === target) || null;
      }

      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return null;
      return this.models.find((model) => model.blockId === blockId) || null;
    }

    showEditorModel(model, ensureVisible = false) {
      if (!designMode || !model || !this.models.includes(model)) return;
      this.editorPreviewActive = true;
      this.currentStep = model.step;

      if (model.path) {
        this.inquiryType = model.path;
        this.checkPathInput(model.path);
      } else if (model.step === 0) {
        this.inquiryType = '';
        this.checkPathInput('');
      } else if (!this.inquiryType) {
        this.inquiryType = this.firstAvailablePath();
        this.checkPathInput(this.inquiryType);
      }

      this.clearPathError();
      this.render(false);
      if (!ensureVisible) return;

      const alignPanel = () => {
        if (!model.panel.isConnected) return;
        model.panel.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
        const header = document.querySelector('[data-site-header]');
        const headerHeight = header
          ? header.getBoundingClientRect().height
          : 0;
        const safeTop = Math.max(112, headerHeight + 24);
        const panelTop = model.panel.getBoundingClientRect().top;
        window.scrollBy({ top: panelTop - safeTop, behavior: 'auto' });
      };

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(alignPanel);
      });
      window.clearTimeout(this.editorAlignTimer);
      this.editorAlignTimer = window.setTimeout(alignPanel, 720);
    }

    setRouteRequired(container, required) {
      container.querySelectorAll('[data-route-required]').forEach((field) => {
        field.required = Boolean(required);
      });
    }

    setManagedControls(container, active) {
      container.querySelectorAll('input, select, textarea, button').forEach((control) => {
        if (!active) {
          if (!control.disabled) {
            control.dataset.lldgManagedDisabled = 'true';
            control.disabled = true;
          }
        } else if (control.dataset.lldgManagedDisabled === 'true') {
          control.disabled = false;
          delete control.dataset.lldgManagedDisabled;
        }
      });
    }

    setPanelState(model, visible, enabled) {
      model.panel.hidden = !visible;
      model.panel.setAttribute('aria-hidden', String(!visible));
      model.panel.classList.toggle('is-active', visible);
      this.setManagedControls(model.panel, enabled);
    }

    updatePathPanels(container, visible, enabled) {
      const pathPanels = Array.from(
        container.querySelectorAll('[data-inquiry-path], [data-path-panel]'),
      );
      pathPanels.forEach((panel) => {
        const path = this.normalizePath(
          panel.dataset.inquiryPath || panel.dataset.pathPanel || '',
        );
        const pathMatches = !path || path === this.inquiryType;
        const active = visible && pathMatches;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
        panel.classList.toggle('is-active', active);
        this.setManagedControls(panel, enabled && pathMatches);
        this.setRouteRequired(panel, enabled && pathMatches);
      });
    }

    updateProgress() {
      const current = this.currentStep + 1;
      const progress = (current / 3) * 100;
      const wrapper = this.root.querySelector('.form-progress, [data-inquiry-progress]');
      if (wrapper) wrapper.setAttribute('aria-label', `Inquiry form step ${current} of 3`);

      const currentLabel = this.root.querySelector(
        '[data-inquiry-current], [data-progress-current]',
      );
      if (currentLabel) currentLabel.textContent = String(current).padStart(2, '0');
      else if (wrapper && wrapper.firstElementChild) {
        wrapper.firstElementChild.textContent = String(current).padStart(2, '0');
      }

      const bar = this.root.querySelector(
        '[data-inquiry-progress-bar], .form-progress i',
      );
      if (bar) bar.style.width = `${progress}%`;
    }

    updateChoiceCards() {
      this.pathInputs().forEach((input) => {
        const selected = this.pathForInput(input) === this.inquiryType;
        input.checked = selected;
        const label = input.closest('label');
        if (label) label.classList.toggle('selected', selected);
      });
    }

    setButtonLabel(button, label) {
      if (!button) return;
      const explicit = button.querySelector(
        '[data-inquiry-button-label], [data-button-label]',
      );
      if (explicit) {
        explicit.textContent = label;
        return;
      }
      const textNode = Array.from(button.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.nodeValue.trim(),
      );
      if (textNode) textNode.nodeValue = `${label} `;
      else button.insertAdjacentText('afterbegin', `${label} `);
    }

    updateControls() {
      if (this.backButton) this.backButton.hidden = this.currentStep === 0;
      if (this.submitButton) {
        if (this.nextButton) this.nextButton.hidden = this.currentStep >= 2;
        this.submitButton.hidden = this.currentStep < 2;
      }

      const target = this.currentStep >= 2 && this.submitButton
        ? this.submitButton
        : this.nextButton;
      if (!target) return;
      const label = this.currentStep < 2
        ? target.dataset.continueLabel || 'Continue'
        : this.inquiryType === 'trade'
          ? this.root.dataset.tradeSubmit ||
            target.dataset.tradeLabel ||
            'Send trade introduction'
          : this.inquiryType === 'general'
            ? this.root.dataset.generalSubmit ||
              target.dataset.generalLabel ||
              'Send general inquiry'
            : this.root.dataset.residentialSubmit ||
              target.dataset.residentialLabel ||
              'Send project inquiry';
      this.setButtonLabel(target, label);
    }

    render(focus = false) {
      this.updateChoiceCards();

      this.models.forEach((model) => {
        const pathMatches = !model.path || model.path === this.inquiryType;
        const visible = model.step === this.currentStep && pathMatches;
        const enabled = this.editorPreviewActive
          ? visible
          : model.step <= this.currentStep && pathMatches;
        this.setPanelState(model, visible, enabled);
        this.setRouteRequired(model.panel, enabled);
        this.updatePathPanels(model.panel, visible, enabled);
      });
      this.updateProgress();
      this.updateControls();
      const editorSubmitNote = this.root.querySelector('[data-editor-submit-note]');
      if (editorSubmitNote && this.currentStep < 2) editorSubmitNote.hidden = true;

      if (focus) {
        window.requestAnimationFrame(() => {
          const activePanel = this.models.find(
            (model) => !model.panel.hidden && model.step === this.currentStep,
          );
          const heading = activePanel && activePanel.panel.querySelector('legend, h3, h2');
          if (heading) {
            if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          }
        });
      }
    }

    showPathError(message) {
      let error = this.root.querySelector('[data-inquiry-error], .form-error');
      if (!error) {
        error = document.createElement('p');
        error.className = 'form-error';
        error.dataset.inquiryError = '';
        error.setAttribute('role', 'alert');
        const choices = this.root.querySelector('.inquiry-choice-grid');
        (choices || this.models[0]?.panel || this.form).insertAdjacentElement(
          choices ? 'afterend' : 'beforeend',
          error,
        );
        this.generatedError = error;
      }
      error.textContent = message;
      error.hidden = false;
    }

    clearPathError() {
      const error = this.root.querySelector('[data-inquiry-error], .form-error');
      if (!error) return;
      error.textContent = '';
      error.hidden = true;
    }

    activePanels() {
      return this.models
        .filter((model) => !model.panel.hidden && model.step === this.currentStep)
        .map((model) => model.panel);
    }

    validateCurrentStep() {
      if (this.currentStep === 0 && !this.inquiryType) {
        this.showPathError('Choose an inquiry type to continue.');
        const first = this.pathInputs()[0];
        if (first) first.focus();
        return false;
      }

      const fields = this.activePanels().flatMap((panel) =>
        Array.from(panel.querySelectorAll('input, select, textarea')).filter(
          (field) => !field.disabled,
        ),
      );
      const invalid = fields.find((field) => !field.checkValidity());
      if (invalid) {
        invalid.reportValidity();
        invalid.focus();
        return false;
      }
      return true;
    }

    advance() {
      if (!this.validateCurrentStep()) return;
      this.editorPreviewActive = false;
      this.clearPathError();
      this.currentStep = Math.min(this.currentStep + 1, 2);
      this.render(true);
    }

    handleClick(event) {
      const back = event.target.closest(
        '[data-inquiry-back], [data-form-back], .form-back',
      );
      if (back && this.form.contains(back)) {
        event.preventDefault();
        this.editorPreviewActive = false;
        this.currentStep = Math.max(this.currentStep - 1, 0);
        this.render(true);
        return;
      }

      const next = event.target.closest('[data-inquiry-next], [data-form-next]');
      if (next && next === this.nextButton && this.submitButton) {
        event.preventDefault();
        this.advance();
      }
    }

    handleChange(event) {
      const field = event.target;
      if (!(field instanceof HTMLInputElement) || field.type !== 'radio') return;
      const path = this.pathForInput(field);
      if (!path) return;
      this.editorPreviewActive = false;
      this.inquiryType = path;
      this.clearPathError();
      this.updateChoiceCards();
    }

    handleSubmit(event) {
      if (this.currentStep < 2) {
        event.preventDefault();
        this.advance();
        return;
      }
      if (!this.validateCurrentStep()) {
        event.preventDefault();
        return;
      }
      if (designMode) {
        event.preventDefault();
        const note = this.root.querySelector('[data-editor-submit-note]');
        if (note) note.hidden = false;
      }
    }

    route(path, focus = false) {
      const normalized = this.normalizePath(path);
      if (!normalized) return;
      this.editorPreviewActive = false;
      this.inquiryType = normalized;
      this.currentStep = 1;
      this.checkPathInput(normalized);
      this.clearPathError();
      this.render(focus);
    }

    destroy() {
      if (!this.form) return;
      window.clearTimeout(this.editorAlignTimer);
      this.form.removeEventListener('click', this.boundClick);
      this.form.removeEventListener('change', this.boundChange);
      this.form.removeEventListener('submit', this.boundSubmit);
      this.models.forEach((model) => {
        model.panel.hidden = model.panel.dataset.lldgOriginalHidden === 'true';
        model.panel.removeAttribute('aria-hidden');
        model.panel.classList.toggle(
          'is-active',
          model.panel.dataset.lldgOriginalActive === 'true',
        );
        delete model.panel.dataset.lldgOriginalActive;
        model.panel.querySelectorAll('[data-lldg-managed-disabled]').forEach((control) => {
          control.disabled = false;
          delete control.dataset.lldgManagedDisabled;
        });
      });
      if (this.nextButton) {
        const originalType = this.nextButton.dataset.lldgOriginalType;
        if (originalType) this.nextButton.setAttribute('type', originalType);
        else this.nextButton.removeAttribute('type');
        delete this.nextButton.dataset.lldgOriginalType;
      }
      this.form.querySelectorAll('[data-route-required]').forEach((field) => {
        field.required = false;
      });
      if (this.generatedError) this.generatedError.remove();
    }
  }

  const canonicalRoot = (element) => {
    if (element.matches('form')) return element.closest('.inquiry-form-shell') || element;
    const form = element.querySelector(
      'form[data-lldg-inquiry-form], form[action*="/contact"], form',
    );
    if (!form) return null;
    const nestedShell = form.closest('.inquiry-form-shell, [data-lldg-inquiry]');
    return nestedShell || element;
  };

  const candidateRoots = (scope) => {
    const candidates = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) candidates.push(scope);
    if (scope && typeof scope.querySelectorAll === 'function') {
      candidates.push(...scope.querySelectorAll(ROOT_SELECTOR));
    }
    return Array.from(
      new Set(candidates.map(canonicalRoot).filter(Boolean)),
    );
  };

  const initialize = (scope = document) => {
    instances.forEach((instance, root) => {
      if (!root.isConnected) {
        instance.destroy();
        instances.delete(root);
      }
    });
    candidateRoots(scope).forEach((root) => {
      if (!instances.has(root)) instances.set(root, new LldgInquiry(root));
    });
  };

  const destroyWithin = (scope) => {
    instances.forEach((instance, root) => {
      if (root === scope || (scope instanceof Element && scope.contains(root))) {
        instance.destroy();
        instances.delete(root);
      }
    });
  };

  const routeAll = (path, focus = false) => {
    if (!path) return;
    instances.forEach((instance) => instance.route(path, focus));
  };

  const initializePage = () => {
    initialize(document);
    const hash = window.location.hash;
    const path =
      hash === '#project-inquiry'
        ? 'residential'
        : hash === '#trade-inquiry'
          ? 'trade'
          : hash === '#general-inquiry'
            ? 'general'
            : '';
    routeAll(path, false);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePage, { once: true });
  } else {
    initializePage();
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    let hash = '';
    try {
      hash = new URL(link.href, window.location.href).hash;
    } catch {
      return;
    }
    const path =
      hash === '#project-inquiry'
        ? 'residential'
        : hash === '#trade-inquiry'
          ? 'trade'
          : hash === '#general-inquiry'
            ? 'general'
            : '';
    routeAll(path, true);
  });

  window.addEventListener('hashchange', () => {
    const path =
      window.location.hash === '#project-inquiry'
        ? 'residential'
        : window.location.hash === '#trade-inquiry'
          ? 'trade'
          : window.location.hash === '#general-inquiry'
            ? 'general'
            : '';
    routeAll(path, false);
  });

  document.addEventListener('lldg:inquiry:refresh', () => initialize(document));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    if (!designMode || !(event.target instanceof Element)) return;
    instances.forEach((instance) => {
      if (event.target.contains(instance.root) || instance.root.contains(event.target)) {
        const first = instance.models.find((model) => model.step === 0) || instance.models[0];
        instance.showEditorModel(first, true);
      }
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    if (!designMode) return;
    instances.forEach((instance) => {
      const model = instance.modelFromEditorEvent(event);
      if (model) instance.showEditorModel(model, true);
    });
  });
})();
